/**
 * Preview copy for the amend covering email.
 * Wording matches services/notifications-manager/src/amendLetter/content.ts.
 * The sent email is rendered on the server from form fields, not from this preview.
 */
function singleLine(value) {
    return value
        .replaceAll(/[\r\n]+/g, ' ')
        .replaceAll(/[ \t]+/g, ' ')
        .trim();
}
/**
 * Directory titles are stored as "SURNAME Firstname (organisation)".
 * The covering email greeting uses the given name. Other names are unchanged.
 * Kept in step with services/notifications-manager/src/amendLetter/content.ts.
 */
export function greetingNameFromHiringManager(name) {
    const trimmed = singleLine(name);
    const withoutOrganisation = trimmed.replace(/\s*\([^)]*\)\s*$/, '').trim();
    const parts = withoutOrganisation
        .split(/\s+/)
        .filter((part) => part.length > 0);
    const surname = parts[0] ?? '';
    const givenName = parts[1] ?? '';
    const surnameIsUpperCase = surname.length > 0 &&
        surname === surname.toUpperCase() &&
        surname !== surname.toLowerCase();
    if (surnameIsUpperCase && givenName) {
        return givenName;
    }
    return trimmed;
}
function isRecord(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
function readString(record, key) {
    const value = record[key];
    return typeof value === 'string' ? value : '';
}
/**
 * First whitespace-separated token of the employee name.
 * A single-word name is used as-is.
 */
export function firstNameFromEmployeeName(employeeName) {
    const trimmed = employeeName.trim();
    if (!trimmed) {
        return '';
    }
    const [firstName] = trimmed.split(/\s+/);
    return firstName ?? '';
}
/**
 * Display name for the HR administrator who sends the letter.
 * Uses Cognito given and family names, then a title-cased email local part.
 */
export function administratorDisplayName(input) {
    const firstName = input.firstName?.trim() ?? '';
    const familyName = input.familyName?.trim() ?? '';
    const combined = [firstName, familyName]
        .filter((part) => part.length > 0)
        .join(' ');
    if (combined.length > 0) {
        return combined;
    }
    return titleCaseEmailLocalPart(input.email ?? '');
}
export function administratorNameFromProfile(profile) {
    if (!isRecord(profile)) {
        return '';
    }
    return administratorDisplayName({
        firstName: readString(profile, 'given_name'),
        familyName: readString(profile, 'family_name'),
        email: readString(profile, 'email'),
    });
}
/**
 * Builds the read-only covering email preview. Returns null when a merge field is missing.
 */
export function buildAmendCoveringEmailPreview(input) {
    const hiringManagerName = greetingNameFromHiringManager(input.hiringManagerName);
    const employeeFullName = singleLine(input.employeeFullName);
    const employeeFirstName = firstNameFromEmployeeName(employeeFullName);
    const administratorName = singleLine(input.administratorName);
    if (!hiringManagerName ||
        !employeeFullName ||
        !employeeFirstName ||
        !administratorName) {
        return null;
    }
    const subject = `Employee Amendment Letter attached for ${employeeFullName}`;
    const intro = `Following your recent request for an employee change, please see attached the Employee Amendment Letter for ${employeeFullName}. Once you have reviewed the letter, please can you share this with ${employeeFirstName}. You may wish to do this as part of a meeting or to simply forward the letter to them if they are aware of and expecting the change. Please note, this letter has only been shared with you therefore any secondary or additional contacts recorded on the SLAM portal will not have received a copy. Any relevant changes have also been updated in the SAP HR and Payroll system.`;
    const itDesk = 'If applicable, please remember to contact the IT Service Desk via Ivanti to arrange access to, or removal of, any new IT systems or equipment.';
    const privacy = 'The content is personal to the employee and therefore you must manage use of the content in line with the Equans Privacy Statement. We will retain a copy of the letter on the employees personal file, therefore please delete any copies once you have reviewed and shared with employee.';
    const queries = 'If you have any further queries in relation to this change, please do not hesitate to get in touch.';
    const text = [
        `Hi ${hiringManagerName}`,
        '',
        intro,
        '',
        itDesk,
        '',
        privacy,
        '',
        queries,
        '',
        'Regards,',
        administratorName,
        'HR Administrator (Hire to Retire)',
    ].join('\n');
    return { subject, text };
}
function titleCaseEmailLocalPart(email) {
    const trimmed = email.trim();
    const atIndex = trimmed.indexOf('@');
    if (atIndex <= 0) {
        return '';
    }
    const localPart = trimmed.slice(0, atIndex);
    const segments = localPart.split('.').filter((segment) => segment.length > 0);
    if (segments.length === 0) {
        return '';
    }
    return segments
        .map((segment) => `${segment.charAt(0).toUpperCase()}${segment.slice(1).toLowerCase()}`)
        .join(' ');
}
